// ✅ ALTERED ONLY FOR THEME (matches your “above theme”)
// - Orange header bar + white text
// - Page bg #F2F2F2 + inner white surfaces with soft blue border
// - Left orange accent stripe for Search/Filters section
// - Inputs/select: soft blue border + orange focus
// - Table: blue header, soft zebra rows, hover tint, subtle borders
// - IconButtons: blue default, orange on hover
// - No business/API logic changed

import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Typography,
  Divider,
  Snackbar,
  Alert,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { API_URL, API_UPLOAD_URL } from "./config";

export default function PassingOutStudents() {
  // ✅ Theme constants (same as ManageUsers)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };

  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };

  const iconBtnSx = {
    borderRadius: 2,
    color: "rgba(25,118,210,0.95)",
    "&:hover": { backgroundColor: "rgba(240,120,40,0.10)", color: THEME_ORANGE },
  };

  const tableWrapSx = {
    ...surfaceSx,
    mt: 2,
    overflow: "hidden",
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
    verticalAlign: "top",
  });

  const checkboxSx = {
    color: "rgba(25,118,210,0.75)",
    "&.Mui-checked": { color: THEME_ORANGE },
  };

  // ===== Original state/logic =====
  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState("");
  const [isActiveFilter, setIsActive] = useState(""); // '', '1', or '0'
  const [campusCode, setCampusCode] = useState("");
  const [username, setUsername] = useState("");
  const token = localStorage.getItem("auth");

  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // pull campusCode from JWT once
  useEffect(() => {
    if (!token) return;
    try {
      const decodedToken = JSON.parse(atob(token.split(".")[1]));
      const uname = decodedToken.username;
      const cc = decodedToken.campusCode;
      setCampusCode(cc);
      setUsername(uname);
    } catch {
      // keep silent as before
    }
  }, []);

  // fetch data on mount or whenever filter/search changes
  useEffect(() => {
    if (!campusCode) return;

    axios
      .get(`${API_URL}/api/PassingOutStudents`, {
        params: {
          IsStudentActive: isActiveFilter,
          Search: search,
        },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setRecords(res.data))
      .catch(() => setError("Failed to load records"));
  }, [campusCode, isActiveFilter, search]);

  // track edited values locally
  const handleChange = (id, field, value) => {
    setRecords((rs) =>
      rs.map((r) => (r.StudentId === id ? { ...r, [field]: value } : r))
    );
  };

  const formatISTDateTime = (value) =>
    value
      ? new Date(value).toLocaleString("en-IN", {
          timeZone: "Asia/Kolkata",
        })
      : "";

  const saveRow = (student) => {
    setError("");

    const docsReturned = !!student.DocumentsReturned;

    if (docsReturned && !String(student.DocumentsReturnedTo || "").trim()) {
      setError("Returned To is mandatory when Return Docs is checked.");
      return;
    }

    axios
      .put(
        `${API_URL}/api/PassingOutStudents/${student.StudentId}`,
        {
          Username: username,
          Semester: student.Semester,
          IsStudentActive: student.IsStudentActive,
          DocumentsReturned: docsReturned ? 1 : 0,
          DocumentsReturnedTo: docsReturned ? student.DocumentsReturnedTo : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => {
        setSuccess(true);

        setRecords((rs) =>
          rs.map((r) =>
            r.StudentId === student.StudentId
              ? {
                  ...r,
                  DocumentsReturned: res.data.DocumentsReturned,
                  DocumentsReturnedTo: res.data.DocumentsReturnedTo,
                  DocumentsReturnedOn: res.data.DocumentsReturnedOn,
                  DocumentsReturnedBy: res.data.DocumentsReturnedBy,
                }
              : r
          )
        );
      })
      .catch((err) => {
        setError(err?.response?.data?.error || "Failed to update student");
      });
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Update Student Status
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Passing-out / document return status management
            </Typography>
          </Box>
        </Box>

        <Box sx={bodySx}>
          {/* ✅ Filters section with left orange stripe */}
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Search & Filters
              </Typography>
              <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <TextField
                  label="Search ID or Name"
                  size="small"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ ...inputSx, minWidth: 260 }}
                />

                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Active Status</InputLabel>
                  <Select
                    label="Active Status"
                    value={isActiveFilter}
                    onChange={(e) => setIsActive(e.target.value)}
                    sx={inputSx}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="1">Active</MenuItem>
                    <MenuItem value="0">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Paper>

          {/* Messages */}
          {error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                mt: 2,
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
              }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Snackbar open autoHideDuration={2500} onClose={() => setSuccess(false)}>
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                Updated successfully!
              </Alert>
            </Snackbar>
          )}

          {/* ✅ Table */}
          {/* OUTER – keeps rounded corners & spacing */}
          <Paper elevation={0} sx={tableWrapSx}>

            {/* INNER – ONLY for horizontal scroll */}
            <Box
              sx={{
                overflowX: "auto",
                overflowY: "hidden",
              }}
            >
          <TableContainer component="div">
            <Table size="small" sx={{ width: "100%" }}>
              <colgroup>
                <col style={{ width: "2%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "8%" }} />
              </colgroup>

              <TableHead>
                <TableRow sx={theadRowSx}>
                  {[
                    "#",
                    "StudentId",
                    "FullName",
                    "Certificate Details",
                    "StorageInfo",
                    "Return Docs",
                    "ReturnedTo",
                    "ReturnedOn",
                    "ReturnedBy",
                    "Semester",
                    "AmountDue",
                    "Active",
                    "Actions",
                  ].map((h) => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {records.map((r, i) => (
                  <TableRow key={r.StudentId} sx={zebraRowSx(i)}>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{r.StudentId}</TableCell>
                  <TableCell>{r.FullName}</TableCell>
                  <TableCell sx={{ minWidth: 260 }}>
                    {(r.DocumentDetails || []).length > 0 ? (
                      <Box>
                        {(r.DocumentDetails || []).map((doc, idx) => (
                          <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                            {idx + 1}. {doc.DocumentType}
                            {doc.IsReceived ? " - Received" : ""}
                            {doc.IsVerified ? " - Verified" : ""}
                          </Typography>
                        ))}
                      </Box>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  
                  <TableCell>{r.DocumentsStoredInfo}</TableCell>
                  <TableCell>
                    <Checkbox
                      checked={!!r.DocumentsReturned}
                      onChange={(e) => {
                        const checked = e.target.checked;
                      
                        setRecords((rs) =>
                          rs.map((row) =>
                            row.StudentId === r.StudentId
                              ? {
                                  ...row,
                                  DocumentsReturned: checked ? 1 : 0,
                                  DocumentsReturnedTo: checked ? row.DocumentsReturnedTo : "",
                                  DocumentsReturnedOn: checked ? row.DocumentsReturnedOn : null,
                                  DocumentsReturnedBy: checked ? row.DocumentsReturnedBy : null,
                                }
                              : row
                          )
                        );
                      }}
                      sx={checkboxSx}
                    />
                  </TableCell>
                    
                  <TableCell>
                    <TextField
                      size="small"
                      value={r.DocumentsReturnedTo || ""}
                      disabled={!r.DocumentsReturned}
                      required={!!r.DocumentsReturned}
                      error={!!r.DocumentsReturned && !String(r.DocumentsReturnedTo || "").trim()}
                      helperText={
                        !!r.DocumentsReturned && !String(r.DocumentsReturnedTo || "").trim()
                          ? "Required"
                          : ""
                      }
                      onChange={(e) =>
                        handleChange(r.StudentId, "DocumentsReturnedTo", e.target.value)
                      }
                      sx={{ ...inputSx, minWidth: 160 }}
                    />
                  </TableCell>
                    
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {formatISTDateTime(r.DocumentsReturnedOn)}
                  </TableCell>
                    
                  <TableCell>{r.DocumentsReturnedBy || ""}</TableCell>
                    
                  <TableCell>
                    <TextField
                      size="small"
                      value={r.Semester || ""}
                      onChange={(e) => handleChange(r.StudentId, "Semester", e.target.value)}
                      sx={{ ...inputSx, width: 80 }}
                    />
                  </TableCell>                   
                  <TableCell
                    sx={{
                      color: r.FeeDue > 0 ? "error.main" : "inherit",
                      fontWeight: r.FeeDue > 0 ? 800 : 400,
                    }}
                  >
                    {r.FeeDue}
                  </TableCell>
                  
                  <TableCell>
                    <Checkbox
                      checked={!!r.IsStudentActive}
                      onChange={(e) =>
                        handleChange(r.StudentId, "IsStudentActive", e.target.checked ? 1 : 0)
                      }
                      sx={checkboxSx}
                    />
                  </TableCell>
                    
                  <TableCell>
                    <IconButton sx={iconBtnSx} onClick={() => saveRow(r)} title="Save">
                      <SaveIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
                ))}

                {!records.length && (
                  <TableRow>
                    <TableCell colSpan={14} sx={{ py: 3, textAlign: "center", opacity: 0.7 }}>
                      No records found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
}
